import React, { useRef, useState, useCallback } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Image,
  Dimensions,
  Animated,
  Platform,
  RefreshControl,
  ActivityIndicator
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import { H1, H2, H3, Body1, Body2, Caption, ButtonText } from '../../components/common/TextStyles';
import { useTheme, getShadow } from '../../theme/useTheme';
import { getLMSSummary } from '../../services/lmsService';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const SomaScreen = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const scrollY = useRef(new Animated.Value(0)).current;
  const [refreshing, setRefreshing] = useState(false);
  const [lmsSummary, setLmsSummary] = useState({ enrolled: 0, completed: 0, certificates: 0, courses: [] });

  const loadLMSSummary = useCallback(async () => {
    const summary = await getLMSSummary();
    setLmsSummary(summary);
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadLMSSummary();
    }, [loadLMSSummary])
  );

  const categories = [
    { icon: 'business', label: 'Business', color: '#6366F1' },
    { icon: 'cash', label: 'Finance', color: '#10B981' },
    { icon: 'laptop', label: 'Tech', color: '#F59E0B' },
    { icon: 'people', label: 'Leadership', color: '#EC4899' },
  ];

  // Header Animation values
  const headerHeight = 185;

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 80],
    outputRange: [1, 0],
    extrapolate: 'clamp',
  });

  const stickyHeaderOpacity = scrollY.interpolate({
    inputRange: [80, 120],
    outputRange: [0, 1],
    extrapolate: 'clamp',
  });

  const onRefresh = async () => {
    setRefreshing(true);
    // Refresh logic here
    setTimeout(() => setRefreshing(false), 1500);
  };

  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      {/* Background Aura Section (Fixed) */}
      <View style={[styles.headerBackground, { height: headerHeight + 50 }]}>
        <LinearGradient
          colors={['#4f46e5', '#6366f1', '#818cf8']}
          style={StyleSheet.absoluteFill}
        />
        <View style={styles.auraContainer}>
          <View style={[styles.aura, styles.aura1]} />
          <View style={[styles.aura, styles.aura2]} />
          <View style={[styles.aura, styles.aura3]} />
        </View>
      </View>

      {/* Mini Sticky Header */}
      <Animated.View
        style={[
          styles.miniStickyHeader,
          { paddingTop: insets.top + 5, opacity: stickyHeaderOpacity }
        ]}
        pointerEvents={Platform.OS === 'web' ? 'auto' : 'none'}
      >
        <LinearGradient colors={['#4f46e5', '#6366f1']} style={StyleSheet.absoluteFill} />
        <View style={styles.miniHeaderContent}>
          <H2 style={styles.miniHeaderTitle}>Learning Hub</H2>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.miniBackBtn}>
            <Ionicons name="chevron-back" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </Animated.View>

      <Animated.ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
        showsVerticalScrollIndicator={false}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: true }
        )}
        scrollEventThrottle={16}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor="#FFFFFF"
            progressViewOffset={insets.top + 20}
          />
        }
      >
        {/* Full Hero Header */}
        <Animated.View style={[styles.heroHeader, { paddingTop: insets.top + 20, opacity: headerOpacity }]}>
          <View style={styles.topBar}>
            <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backCircle}>
              <Ionicons name="arrow-back" size={22} color="#FFFFFF" />
            </TouchableOpacity>
            <View style={styles.headerIcons}>
              <TouchableOpacity style={styles.iconCircle}>
                <Ionicons name="search" size={20} color="#FFFFFF" />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.heroTitleBlock}>
            <Caption style={styles.heroSubtitle}>SOMA • EMPOWER</Caption>
            <H2 style={styles.heroTitle}>Your Skills Hub</H2>
          </View>

          {/* Compact Stats Row */}
          <View style={styles.statsGridCompact}>
            <View style={styles.statPill}>
              <Ionicons name="book" size={14} color="#FFFFFF" />
              <Caption style={styles.statPillText}>
                {lmsSummary.requiresLogin ? '--' : lmsSummary.enrolled} Courses
              </Caption>
            </View>
            <View style={styles.statPill}>
              <Ionicons name="ribbon" size={14} color="#FFFFFF" />
              <Caption style={styles.statPillText}>
                {lmsSummary.requiresLogin ? 'LMS Locked' : `${lmsSummary.completed} Completed`}
              </Caption>
            </View>
          </View>
        </Animated.View>

        {/* Content Surface */}
        <View style={styles.contentSurface}>

          {/* Partner Section - Intuit LMS */}
          <View style={styles.partnerSection}>
            <TouchableOpacity
              style={styles.lmsPremiumCard}
               onPress={() => navigation.navigate('IntuitLMS')}
              activeOpacity={0.9}
            >
              <LinearGradient colors={['#1e1b4b', '#4338ca']} style={styles.lmsGradient}>
                <View style={styles.lmsTextContent}>
                  <View style={styles.lmsBadge}>
                    <Caption style={styles.lmsBadgeText}>CERTIFIED PARTNER</Caption>
                  </View>
                  <H2 style={styles.lmsTitle}>Intuit LMS</H2>
                  <Body2 style={styles.lmsDesc}>University-grade education tailored for future leaders.</Body2>
                  <View style={styles.lmsBtn}>
                    <Caption style={styles.lmsBtnText}>OPEN PORTAL</Caption>
                    <Ionicons name="chevron-forward" size={12} color="#FFFFFF" />
                  </View>
                </View>
                <View style={styles.lmsIconFloat}>
                  <Ionicons name="school" size={70} color="rgba(255,255,255,0.1)" />
                </View>
              </LinearGradient>
            </TouchableOpacity>

            {/* My Learning Progress shortcut */}
            <TouchableOpacity
              style={styles.progressShortcut}
              onPress={() => navigation.navigate('LMSProgress')}
              activeOpacity={0.85}
            >
              <View style={styles.progressShortcutLeft}>
                <View style={styles.progressShortcutIcon}>
                  <Ionicons name="bar-chart" size={16} color="#6366F1" />
                </View>
                <View>
                  <Caption style={styles.progressShortcutTitle}>My Learning Progress</Caption>
                  <Caption style={styles.progressShortcutSub}>Track your course progress and transcripts</Caption>
                </View>
              </View>
              <Ionicons name="chevron-forward" size={16} color="#9CA3AF" />
            </TouchableOpacity>
          </View>

          {/* Categories */}
          <View style={styles.categoriesSection}>
            <H3 style={styles.surfaceTitle}>Explore Categories</H3>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.catScroll}>
              {categories.map((cat, idx) => (
                <TouchableOpacity key={idx} style={styles.catItem}>
                  <View style={[styles.catIconWrap, { backgroundColor: cat.color + '15' }]}>
                    <Ionicons name={cat.icon} size={22} color={cat.color} />
                  </View>
                  <Caption style={styles.catLabel}>{cat.label}</Caption>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          {/* Enrolled Courses */}
          <View style={styles.coursesSection}>
            <View style={styles.sectionHeaderRow}>
              <H3 style={styles.surfaceTitle}>Continue Learning</H3>
              <TouchableOpacity onPress={() => navigation.navigate('LMSProgress')}>
                <Caption style={styles.viewAll}>View All</Caption>
              </TouchableOpacity>
            </View>

            {/* Courses List */}
            {lmsSummary.requiresLogin ? (
              /* Portal Sync Prompt */
              <TouchableOpacity
                style={styles.emptyCoursesCard}
                onPress={() => navigation.navigate('IntuitLMS', { screen: 'select-institution' })}
              >
                <Ionicons name="lock-closed" size={32} color="#6366F1" />
                <Body2 style={styles.emptyCoursesText}>
                  Login to the Intuit Portal to sync your learning progress.
                </Body2>
                <View style={[styles.lmsBtn, { marginTop: 10, backgroundColor: '#6366F1' }]}>
                  <Caption style={styles.lmsBtnText}>SYNC NOW</Caption>
                </View>
              </TouchableOpacity>
            ) : lmsSummary.courses && lmsSummary.courses.length > 0 ? (
              lmsSummary.courses.slice(0, 3).map((course) => (
                <TouchableOpacity
                  key={course.courseId}
                  style={styles.courseCardModern}
                  onPress={() => navigation.navigate('IntuitLMS', { screen: 'units' })}
                  activeOpacity={0.85}
                >
                  <Image
                    source={{ uri: `https://picsum.photos/seed/${course.courseId}/600/400` }}
                    style={styles.courseImg}
                  />
                  <View style={styles.courseDetailOverlay}>
                    <View style={styles.courseHeader}>
                      <View style={[styles.courseCatBadge, { backgroundColor: '#6366F1' }]}>
                        <Caption style={styles.courseCatText}>LMS MODULE</Caption>
                      </View>
                    </View>
                    <View style={styles.courseContent}>
                      <H3 style={styles.courseTitleText}>{course.courseName}</H3>
                      <View style={styles.progressContainer}>
                        <View style={styles.progressBg}>
                          <View style={[styles.progressFill, { width: `${course.progressPercent}%`, backgroundColor: '#6366F1' }]} />
                        </View>
                        <Caption style={styles.progressPercent}>{course.progressPercent}%</Caption>
                      </View>
                    </View>
                  </View>
                </TouchableOpacity>
              ))
            ) : (
              /* Placeholder for no courses */
              <View style={styles.emptyCoursesCard}>
                <Ionicons name="school-outline" size={32} color="#D1D5DB" />
                <Body2 style={styles.emptyCoursesText}>Start your learning journey in the portal.</Body2>
              </View>
            )}

            <TouchableOpacity
              style={styles.viewMoreBtn}
              onPress={() => navigation.navigate('LMSProgress')}
            >
              <Caption style={styles.viewMoreText}>View more in My Progress</Caption>
              <Ionicons name="chevron-forward" size={12} color="#6366F1" />
            </TouchableOpacity>
          </View>

          {/* Empty Space / Footer */}
          <View style={styles.footerSection}>
            <Caption style={styles.footerText}>Innovation in Learning • Youth Connect v1.2</Caption>
          </View>

        </View>
      </Animated.ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  headerBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 0,
  },
  auraContainer: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  aura: {
    position: 'absolute',
    borderRadius: 120,
    opacity: 0.2,
  },
  aura1: {
    width: 200,
    height: 200,
    top: -40,
    right: -20,
    backgroundColor: '#C084FC',
  },
  aura2: {
    width: 160,
    height: 160,
    top: 40,
    left: -40,
    backgroundColor: '#818CF8',
  },
  aura3: {
    width: 140,
    height: 140,
    bottom: -50,
    right: 30,
    backgroundColor: '#4C1D95',
  },
  miniStickyHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    height: 85,
    elevation: 4,
  },
  miniHeaderContent: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    height: 42,
    marginTop: 10,
  },
  miniHeaderTitle: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  miniBackBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollView: {
    flex: 1,
    zIndex: 1,
  },
  scrollViewContent: {
    paddingBottom: 0,
  },
  heroHeader: {
    paddingHorizontal: 18,
    paddingBottom: 30,
    zIndex: 2,
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  backCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.25)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroTitleBlock: {
    marginBottom: 16,
  },
  heroSubtitle: {
    color: 'rgba(255,255,255,0.8)',
    letterSpacing: 1.5,
    fontWeight: '800',
    fontSize: 10,
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '800',
    marginTop: 2,
  },
  statsGridCompact: {
    flexDirection: 'row',
    gap: 10,
  },
  statPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 10,
  },
  statPillText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 10,
  },
  contentSurface: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingTop: 20,
    minHeight: SCREEN_HEIGHT - 80,
    zIndex: 5,
    marginTop: -16,
  },
  partnerSection: {
    paddingHorizontal: 18,
    marginBottom: 20,
  },
  lmsPremiumCard: {
    borderRadius: 18,
    overflow: 'hidden',
    ...getShadow('md'),
  },
  lmsGradient: {
    flexDirection: 'row',
    padding: 16,
  },
  lmsTextContent: {
    flex: 1,
    zIndex: 2,
  },
  lmsBadge: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 5,
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  lmsBadgeText: {
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: '900',
  },
  lmsTitle: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '800',
    marginBottom: 3,
  },
  lmsDesc: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 12,
    lineHeight: 16,
    marginBottom: 14,
  },
  lmsBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
    alignSelf: 'flex-start',
  },
  lmsBtnText: {
    color: '#FFFFFF',
    fontWeight: '900',
    fontSize: 9,
  },
  lmsIconFloat: {
    position: 'absolute',
    right: -10,
    bottom: -10,
    zIndex: 1,
  },
  categoriesSection: {
    paddingHorizontal: 18,
    marginBottom: 22,
  },
  surfaceTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#111827',
    marginBottom: 14,
  },
  catScroll: {
    paddingLeft: 0,
  },
  catItem: {
    alignItems: 'center',
    marginRight: 16,
    width: 56,
  },
  catIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6,
  },
  catLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#6B7280',
    textAlign: 'center',
  },
  coursesSection: {
    paddingHorizontal: 18,
    marginBottom: 22,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  viewAll: {
    color: '#6366F1',
    fontWeight: '800',
    fontSize: 12,
  },
  courseCardModern: {
    height: 170,
    borderRadius: 18,
    overflow: 'hidden',
    marginBottom: 14,
    backgroundColor: '#F3F4F6',
    ...getShadow('sm'),
  },
  courseImg: {
    ...StyleSheet.absoluteFillObject,
  },
  courseDetailOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.35)',
    padding: 14,
    justifyContent: 'space-between',
  },
  courseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  courseCatBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  courseCatText: {
    color: '#FFFFFF',
    fontWeight: '900',
    fontSize: 8,
  },
  favBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  courseContent: {
    gap: 4,
  },
  courseTitleText: {
    color: '#FFFFFF',
    fontSize: 17,
    fontWeight: '800',
  },
  courseMeta: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 8,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    color: 'rgba(255,255,255,0.8)',
    fontWeight: '600',
    fontSize: 11,
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  progressBg: {
    flex: 1,
    height: 3,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  progressPercent: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 9,
  },
  footerSection: {
    alignItems: 'center',
    paddingVertical: 30,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    marginHorizontal: 18,
  },
  footerText: {
    color: '#D1D5DB',
    fontSize: 10,
  },

  // ── My Learning Progress Shortcut ───────────────────────────────────────
  progressShortcut: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: 14,
    marginTop: 10,
    ...getShadow('sm'),
    borderWidth: 1,
    borderColor: '#EEF2FF',
  },
  progressShortcutLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  progressShortcutIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: '#EEF2FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressShortcutTitle: {
    color: '#111827',
    fontWeight: '800',
    fontSize: 13,
  },
  progressShortcutSub: {
    color: '#9CA3AF',
    fontSize: 11,
    marginTop: 2,
  },

  // ── LMS Course Cards (compact) ───────────────────────────────────────────
  lmsCourseCard: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    marginBottom: 10,
    overflow: 'hidden',
    ...getShadow('sm'),
  },
  lmsCourseAccent: {
    width: 4,
    height: '100%',
  },
  lmsCourseBody: {
    flex: 1,
    padding: 12,
    gap: 6,
  },
  lmsCourseTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  lmsCourseName: {
    flex: 1,
    color: '#1F2937',
    fontWeight: '700',
    fontSize: 13,
  },
  lmsCoursePercent: {
    fontSize: 12,
    fontWeight: '800',
    marginLeft: 8,
  },
  lmsCourseUnits: {
    color: '#9CA3AF',
    fontSize: 11,
  },
  lmsCourseProgressBg: {
    height: 4,
    backgroundColor: '#F3F4F6',
    borderRadius: 2,
    overflow: 'hidden',
  },
  lmsCourseProgressFill: {
    height: '100%',
    borderRadius: 2,
  },

  // ── Empty / Loading States ───────────────────────────────────────────────
  coursesLoadingState: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 20,
    justifyContent: 'center',
  },
  coursesLoadingText: {
    color: '#9CA3AF',
    fontSize: 12,
  },
  emptyCoursesCard: {
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: 14,
    padding: 20,
    gap: 10,
    borderWidth: 1,
    borderColor: '#F3F4F6',
    borderStyle: 'dashed',
  },
  emptyCoursesText: {
    color: '#9CA3AF',
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 18,
  },
  emptyCoursesBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
  emptyCoursesBtnText: {
    color: '#6366F1',
    fontWeight: '800',
    fontSize: 12,
  },
  viewMoreBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 10,
  },
  viewMoreText: {
    color: '#6366F1',
    fontWeight: '700',
    fontSize: 11,
  },
});

export default SomaScreen;

